import CalendarIcon from "@/assets/calendar.svg";
import EditIcon from "@/assets/edit.svg";
import HeartIcon from "@/assets/heart.svg";
import MsgIcon from "@/assets/msg-none.svg";
import TrashIcon from "@/assets/trash.svg";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { RouterOutputs } from "@/lib/trpc";

import { useMe } from "@/context/user-context";
import { useOpenMessageThread } from "@/hooks/use-open-message-thread";
import { useTranslationUtils } from "@/hooks/use-translation-utils";
import { useTRPC } from "@/lib/trpc";
import { formatDate, formatYear } from "@/lib/utils";
import { routes } from "@/routes";

import { Button } from "../ui/button";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "../ui/sheet";
import { UpsertTournamentForm } from "./upsert-tournament";

export const ArchiveTournamentConfirmationModal = ({ modalOpen, closeModal, onClick, isPending }: { modalOpen: boolean; closeModal: () => void; onClick: () => void; isPending: boolean }) => {
  const { t } = useTranslation(["common", "tournaments"]);
  return (
    <Sheet open={modalOpen} onOpenChange={closeModal}>
      <SheetContent side="center" className="h-64 md:h-80">
        <SheetHeader>
          <SheetTitle>{t("tournaments:delete-tournament")}</SheetTitle>
        </SheetHeader>

        <div className="flex flex-1 items-center justify-center">
          <span className="text-balance px-2 text-center text-lg font-medium">
            {t("tournaments:delete-tournament-confirmation")}
          </span>
        </div>

        <SheetFooter>
          <SheetClose asChild>
            <Button variant="dark" type="button">
              {t("common:cancel")}
            </Button>
          </SheetClose>
          <Button
            type="button"
            onClick={onClick}
            loading={isPending}
          >
            {t("common:logout-confirm-btn")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export function TournamentInfos({ tournament }: { tournament: RouterOutputs["tournament"]["getTournamentList"][number] }) {
  const { getCountryName } = useTranslationUtils();
  const { user } = useMe();
  const { i18n, t } = useTranslation(["common", "tournaments"]);
  const locale = i18n.language;
  const [modalState, updateModalState] = useState<"open" | "close">("close");
  const [confirmationModalState, updateConfirmationModalState] = useState<"open" | "close">("close");
  const navigate = useNavigate();
  const openMessageThread = useOpenMessageThread();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.tournament.archiveTournament.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournament.getTournamentList.queryFilter());
      navigate(routes.tournaments);
    },
  }));
  const { mutate: updateFavoriteMutation } = useMutation(trpc.player.updateFavoritesTournaments.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.player.getIsTournamentFavorite.queryFilter({ tournamentId: tournament.id }));
    },
  }));

  function archiveTournament() {
    mutate({ id: tournament.id });
  }
  const { data } = useQuery(trpc.player.getIsTournamentFavorite.queryOptions(
    { tournamentId: tournament.id },
    { enabled: user.role === "player" },
  ));

  const updateFavorite = () => {
    updateFavoriteMutation({
      tournamentId: tournament.id,
    });
  };

  function upsertThread() {
    openMessageThread(tournament.clubRepresentativeId);
  }

  return (
    <>
      <div className="flex flex-col gap-3 px-2 py-3">
        <div className="flex flex-col items-center justify-center gap-2 p-3">
          <h1 className="text-center text-2xl font-semibold uppercase md:px-16">{tournament.name}</h1>

          <div className="flex items-center gap-2">
            <span className="font-medium">{tournament.city}</span>
            <div className="flex w-auto max-w-48 items-center gap-2 rounded-full bg-neutral-100 p-0.5">
              <CircleFlag countryCode={tournament.country.toLowerCase()} className="size-5 rounded-full" />
              <span className="truncate pr-3 text-sm font-medium">
                {getCountryName(tournament.country)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 rounded-xl bg-neutral-50 p-4">
          <CalendarIcon className="shrink-0 text-orange-500" />

          <div className="flex w-full flex-col items-center text-nowrap py-2 leading-5">
            <span className="font-semibold">
              {formatDate(tournament.startDate, locale)}
            </span>
            <span className="text-sm font-medium text-neutral-500">{formatYear(tournament.startDate, locale)}</span>
          </div>

          <ArrowRight className="size-6 shrink-0 text-orange-500" />

          <div className="flex w-full flex-col items-center text-nowrap py-2 leading-5">
            <span className="font-semibold">
              {formatDate(tournament.endDate, locale)}
            </span>
            <span className="text-sm font-medium text-neutral-500">{formatYear(tournament.endDate, locale)}</span>
          </div>
        </div>

        {(user.role === "club" || tournament.description.length > 0) && (
          <p className="whitespace-pre-line text-balance text-center text-sm text-neutral-600">
            {tournament.description.length > 0 ? tournament.description : t("common:no-description")}
          </p>
        )}

        <div className="flex items-center gap-2">
          {user.role === "club" && (
            <>
              <Sheet open={modalState === "open"} onOpenChange={isOpen => updateModalState(isOpen ? "open" : "close")}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="w-full">
                    <EditIcon />
                    {t("common:edit")}
                  </Button>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>
                      {t("tournaments:edit-tournament")}
                    </SheetTitle>
                  </SheetHeader>

                  <UpsertTournamentForm
                    tournament={tournament}
                    closeModal={() => updateModalState("close")}
                  />

                </SheetContent>
              </Sheet>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => updateConfirmationModalState("open")}
              >
                <TrashIcon />
                {t("common:delete")}
              </Button>
            </>
          )}

          {user.role === "player" && (
            <>
              <Button
                onClick={updateFavorite}
                variant="outline"
                className="w-full"
              >
                <HeartIcon
                  className={
                    data?.isFavorite
                      ? "fill-[#FE5000] "
                      : "fill-white "
                  }
                />
                {t("common:mobile-nav.favorites")}
              </Button>

              <Button
                variant="outline"
                className="w-full"
                onClick={upsertThread}
              >
                <MsgIcon />
                {t("common:register")}
              </Button>
            </>
          )}

        </div>
      </div>

      <ArchiveTournamentConfirmationModal
        modalOpen={confirmationModalState === "open"}
        closeModal={() => updateConfirmationModalState("close")}
        onClick={archiveTournament}
        isPending={isPending}
      />
    </>
  );
}
